// Admin Access Layer — the single source of truth for platform-admin
// capabilities. This is the PLATFORM admin layer (PadPulse-internal staff
// accessing /admin and individually authorized Developer Tools) — NOT the
// org/tenant-level roles hosts assign within their own teams.
//
// `super_admin` stays a DB-only master override: no application path may set
// or grant it, and it transparently implies every capability below. Granular
// capabilities live in `app_users.admin_permissions text[]` and are granted /
// revoked only by super-admin assignment surfaces (or directly in SQL).

export const ADMIN_CAPABILITIES = [
  "impersonate_users",
  "view_marketing_materials",
  "manage_marketing_materials",
  "upload_marketing_data",
  "manage_beta_access",
] as const;

export type AdminCapability = (typeof ADMIN_CAPABILITIES)[number];

// Named presets used by the Admin Users assignment UI. Storage stays granular
// (the preset expands to individual capabilities at grant time).
export const ADMIN_PERMISSION_PRESETS = {
  SUPPORT: ["impersonate_users"],
  MARKETING: [
    "view_marketing_materials",
    "manage_marketing_materials",
    "upload_marketing_data",
  ],
} as const satisfies Record<string, readonly AdminCapability[]>;

type AdminLike = {
  isAdmin?: boolean | null;
  superAdmin?: boolean | null;
  adminPermissions?: string[] | null;
};

/** In-app tool authorization only. Does not imply beta participation or gate bypass. */
export function canManageBetaAccess(user: AdminLike | null | undefined): boolean {
  return user?.isAdmin === true || adminCan(user, "manage_beta_access");
}

export type BetaToolAccessSource = "direct" | "platform_admin" | "super_admin";
export type BetaToolManager = {
  id: number;
  email: string;
  name: string | null;
  sources: BetaToolAccessSource[];
};

export function betaToolAccessSources(user: AdminLike): BetaToolAccessSource[] {
  const sources: BetaToolAccessSource[] = [];
  if (user.adminPermissions?.includes("manage_beta_access")) sources.push("direct");
  if (user.isAdmin === true) sources.push("platform_admin");
  if (user.superAdmin === true) sources.push("super_admin");
  return sources;
}

// The single authorization helper every admin-gated route and nav item flows
// through. Unknown strings in admin_permissions are ignored safely.
export function adminCan(user: AdminLike | null | undefined, capability: AdminCapability): boolean {
  if (!user) return false;
  if (user.superAdmin === true) return true;
  return Array.isArray(user.adminPermissions) && user.adminPermissions.includes(capability);
}

// Who counts as an "admin user" (eligible to log into the /admin panel):
// super_admin OR at least one /admin capability. The in-app-only Beta Access
// permission NEVER establishes an /admin session, even with an old admin
// password. Logging in additionally
// requires a non-null admin_password_hash — that check lives at the endpoint.
export function isAdminUser(user: AdminLike | null | undefined): boolean {
  if (!user) return false;
  return user.superAdmin === true || (Array.isArray(user.adminPermissions) &&
    user.adminPermissions.some(capability => capability !== "manage_beta_access"));
}

export function isValidAdminCapability(value: string): value is AdminCapability {
  return (ADMIN_CAPABILITIES as readonly string[]).includes(value);
}
